import { BusinessProfileResponse } from '@/apis/business/types';
import { A2pCampaignPayload } from '@/apis/business/setting/phoneNumbers/types';
import { STATE_OPTIONS } from './hostSmsConstants';
import { A2pBrandFormValues, VERTICAL_OPTIONS } from './a2pConstants';

const US_ALIASES = ['us', 'usa', 'united states', 'united states of america'];
const CA_ALIASES = ['ca', 'canada'];

export type HostSmsBusinessDefaults = {
  customer_name: string;
  address_line1: string;
  city: string;
  zip: string;
  state: string;
  country: string;
};

const CAMPAIGN_VERTICALS = [
  'COMMUNICATION',
  'HEALTHCARE',
  'RETAIL',
  'TECHNOLOGY',
  'FINANCIAL',
  'EDUCATION',
  'REAL_ESTATE',
  'HOSPITALITY'
] as const;

export const hostSmsCountryCode = (country?: string | null): 'US' | 'CA' | '' => {
  const normalized = (country || '').trim().toLowerCase();
  if (US_ALIASES.includes(normalized)) return 'US';
  if (CA_ALIASES.includes(normalized)) return 'CA';
  const upper = (country || '').trim().toUpperCase();
  if (upper === 'US' || upper === 'CA') return upper;
  return '';
};

export const hostSmsStateCode = (state?: string | null, country?: string | null): string => {
  const raw = (state || '').trim();
  if (!raw) return '';

  const countryCode = hostSmsCountryCode(country);
  const options = countryCode ? STATE_OPTIONS[countryCode] || [] : [...STATE_OPTIONS.US, ...STATE_OPTIONS.CA];
  const byValue = options.find((option) => option.value.toLowerCase() === raw.toLowerCase());
  if (byValue) return byValue.value;
  const byLabel = options.find((option) => option.label.toLowerCase() === raw.toLowerCase());
  return byLabel?.value || '';
};

export const hostSmsDefaultsFromBusiness = (business?: BusinessProfileResponse | null): HostSmsBusinessDefaults => {
  const address = business?.address || business?.locations?.find((location) => location.address)?.address || null;
  const country = hostSmsCountryCode(address?.country) || 'US';

  return {
    customer_name: (business?.legal_name || business?.name || '').trim(),
    address_line1: (address?.street_address || '').trim(),
    city: (address?.city || '').trim(),
    zip: (address?.postal_code || '').trim(),
    state: hostSmsStateCode(address?.state, country),
    country
  };
};

const matchVertical = (niche: string | null | undefined, allowed: readonly string[]): string => {
  const raw = (niche || '').trim();
  if (!raw) return '';
  const normalized = raw.toUpperCase().replace(/[\s-]+/g, '_');
  const exact = allowed.find((value) => value === normalized);
  if (exact) return exact;
  const byLabel = allowed.find((value) => value.replace(/_/g, ' ').toLowerCase() === raw.toLowerCase());
  return byLabel || '';
};

const matchCampaignVertical = (niche?: string | null): string => matchVertical(niche, CAMPAIGN_VERTICALS);

const matchBrandVertical = (niche?: string | null): string =>
  matchVertical(niche, VERTICAL_OPTIONS.map((option) => option.value).filter(Boolean));

const formatBusinessPhone = (phone?: number | string | null): string => {
  if (phone == null || phone === '') return '';
  const raw = String(phone).trim();
  if (!raw) return '';
  if (raw.startsWith('+')) return raw;
  const digits = raw.replace(/\D/g, '');
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
  return raw;
};

export const brandDefaultsFromBusiness = (business?: BusinessProfileResponse | null): Partial<A2pBrandFormValues> => {
  if (!business) return {};

  const address = business.address || business.locations?.find((location) => location.address)?.address || null;
  const country = hostSmsCountryCode(address?.country);
  const displayName = (business.name || business.legal_name || '').trim();
  const companyName = (business.legal_name || business.name || '').trim();
  const email = (business.email || '').trim();

  return {
    display_name: displayName,
    company_name: companyName,
    phone: formatBusinessPhone(business.phone),
    email,
    business_contact_email: email,
    street: (address?.street_address || '').trim(),
    city: (address?.city || '').trim(),
    country,
    state: hostSmsStateCode(address?.state, country),
    postal_code: (address?.postal_code || '').trim(),
    vertical: matchBrandVertical(business.niche),
    website: (business.website || '').trim()
  };
};

export const campaignDefaultsFromBusiness = (
  business?: BusinessProfileResponse | null
): Partial<A2pCampaignPayload> => {
  if (!business) return {};

  const name = (business.legal_name || business.name || '').trim();
  const website = (business.website || '').trim();
  const phone = business.phone != null ? String(business.phone).trim() : '';
  const contact = [website, phone].filter(Boolean).join(' · ');

  return {
    name: name ? `${name} Campaign` : '',
    vertical: matchCampaignVertical(business.niche),
    description: name
      ? `${name} uses this campaign to send customer care and account notifications related to our products and services.`
      : '',
    help_message: name
      ? `${name}: For help, reply HELP${contact ? ` or contact us (${contact})` : ''}. Msg & data rates may apply.`
      : '',
    optout_message: name
      ? `${name}: You are unsubscribed and will receive no further messages. Reply START to resubscribe.`
      : '',
    optin_message: name
      ? `${name}: You are subscribed to receive messages. Msg & data rates may apply. Reply HELP for help, STOP to cancel.`
      : '',
    message_flow: name
      ? `Customers opt in through ${name}'s website, verbal consent, or keyword. ${name} sends customer care and account notifications. Reply STOP to unsubscribe and HELP for help.`
      : 'Customers opt in through the website or verbal consent. This campaign sends customer care and account notifications. Reply STOP to unsubscribe and HELP for help.'
  };
};
